// ─────────────────────────────────────────────────────────────────────────────
// Registro de las secciones del Admin de Fundamentales (Programa de Reconocimiento).
//
// PARIDAD DEMO (fuente de verdad): el demo pinta estos módulos como SUBMENÚ del sidebar del
// Panel Admin — `public/plataforma/index.html : 307-312` (`.sidebar-nav--fund`) +
// `js/fundamentales-admin.js : 6-19, 1402-1411` (`.fund-subtabs`) —, no como nav horizontal.
// Este registro se extrajo de `AdminView.tsx` para que lo consuma `views/AdminView` (el Panel
// Admin de Pulso, que es quien dibuja ese sidebar) sin crear un ciclo de imports.
//
// El orden, las etiquetas y los iconos replican la lista `modules` del demo, sin "Reconocimientos"
// (2026-08-23, decisión de Adriana): esa entrada era una puerta de solo lectura al catálogo de
// Pulso (D-27, `AdminCatalogo.tsx`, ya retirado) y quedaba duplicada con el nuevo "Catálogo de
// Fundamentales" del grupo Admin General:
//   roles, puntos, moderacion, ganadores, campanas, reporteria, gestion, notif, vistas, docs,
//   wrapped(Recap)
// ─────────────────────────────────────────────────────────────────────────────
import { AdminRoles } from './AdminRoles';
import { AdminVistas } from './AdminVistas';
import { AdminPoints } from './AdminPoints';
import { AdminModeration } from './AdminModeration';
import { AdminWinners } from './AdminWinners';
import { AdminCampaigns } from './AdminCampaigns';
import { AdminGestion } from './AdminGestion';
import { AdminNotifications } from './AdminNotifications';
import { AdminRecap } from './AdminRecap';
import { AdminDocumentacion } from './AdminDocumentacion';
import { AdminPlaceholder } from './AdminPlaceholder';
import { RecognitionReports } from '../analytics/RecognitionAnalyticsView';
import { TeamContent } from '../metrics/TeamView';
import { TableroContent } from '../metrics/TableroView';
import type { RecognitionAdminSection } from '@/auth/accessMatrix';

export interface SectionDef {
  slug: RecognitionAdminSection;
  icon: string;
  labelKey: string;
}

/**
 * Los 12 módulos del demo, en su orden y con sus iconos (fundamentales-admin.js : 6-19).
 *
 * El ORDEN de referencia para el gating vive en `RECOGNITION_SECTION_ORDER`
 * (`contexts/UserContext.tsx`); aquí van el icono y la etiqueta, que son de la capa de vistas.
 * Los dos listados deben coincidir — lo comprueba `__tests__/adminSections.test.ts`.
 */
export const ADMIN_SECTIONS: SectionDef[] = [
  { slug: 'roles',           icon: 'fa-shield-alt', labelKey: 'admin.nav.roles'         },
  { slug: 'puntos',          icon: 'fa-calculator', labelKey: 'admin.nav.points'        },
  { slug: 'moderacion',      icon: 'fa-gavel',      labelKey: 'admin.nav.moderation'    },
  { slug: 'ganadores',       icon: 'fa-trophy',     labelKey: 'admin.nav.winners'       },
  { slug: 'campanas',        icon: 'fa-bullhorn',   labelKey: 'admin.nav.campaigns'     },
  { slug: 'reporteria',      icon: 'fa-chart-bar',  labelKey: 'admin.nav.reports'       },
  { slug: 'gestion',         icon: 'fa-comments',   labelKey: 'admin.nav.management'    },
  { slug: 'notificaciones',  icon: 'fa-bell',       labelKey: 'admin.nav.notifications' },
  { slug: 'vistas',          icon: 'fa-eye',        labelKey: 'admin.nav.views'         },
  { slug: 'documentacion',   icon: 'fa-file-pdf',   labelKey: 'admin.nav.docs'          },
  { slug: 'recap',           icon: 'fa-star',       labelKey: 'admin.nav.recap'         },
];

/**
 * Slugs que siguen resolviendo por deep-link pero NO aparecen en el menú, porque el demo no
 * los tiene en el submenú de Fundamentales: `equipo` y `tablero` son pestañas de la página de
 * Reconocimiento en el demo (ReconocimientoPage.tsx : 1074-1088), no secciones de Admin.
 * Entraron aquí en FASE 3 al retirar la nav horizontal; se conservan navegables por URL
 * (`/app/equipo`, `/app/tablero` y `/app/admin/<slug>`) mientras se les devuelve su lugar del
 * demo. Ver CONTRATO-RUTAS.md §7.
 */
export const LEGACY_SECTION_SLUGS = ['equipo', 'tablero'];

/** Todos los slugs que el router acepta (menú + deep-links legacy). */
export const ALL_ADMIN_SLUGS = [
  ...ADMIN_SECTIONS.map((s) => s.slug),
  ...LEGACY_SECTION_SLUGS,
];

/** Secciones aún sin implementación real (estado vacío honesto, nunca datos inventados).
 *  🔴 WP-F3 (2026-08-10) la dejó VACÍA: `vistas` era la última y ya tiene pantalla real. Se conserva
 *  el mecanismo —no la entrada— porque es el sitio correcto si vuelve a hacer falta uno. */
const PLACEHOLDERS: Record<string, { req: string; icon: string; titleKey: string; descKey: string }> = {};

/** Devuelve el contenido de una sección; `null` si el slug no existe. */
export function renderAdminSection(slug: string) {
  if (slug === 'roles') return <AdminRoles />;
  if (slug === 'vistas') return <AdminVistas />;
  if (slug === 'puntos') return <AdminPoints />;
  // WP-15 REQ-130 — Moderación (reportes + palabras bloqueadas + toggles).
  if (slug === 'moderacion') return <AdminModeration />;
  // WP-15 REQ-131 — Ganadores (tabla + carga CSV).
  if (slug === 'ganadores') return <AdminWinners />;
  // WP-15 REQ-132 — Campañas (lista + wizard 5 pasos).
  if (slug === 'campanas') return <AdminCampaigns />;
  // WP-08 REQ-133 — Reportería: vista real de 5 tabs (misma que la ruta /app/reportes).
  if (slug === 'reporteria') return <RecognitionReports />;
  // WP-15 REQ-134 — Gestión (ajustes globales, insignias, freeze).
  if (slug === 'gestion') return <AdminGestion />;
  // FASE 7 (paridad demo, H-93..H-95) — Admin › Notificaciones.
  if (slug === 'notificaciones') return <AdminNotifications />;
  // Recap anual (módulo `wrapped` del demo): ediciones, slides, mensaje de respaldo e historial.
  if (slug === 'recap') return <AdminRecap />;
  // WP-16 REQ-137/063 — Documentación: documentos legales versionados + rail de ayuda.
  if (slug === 'documentacion') return <AdminDocumentacion />;
  // Deep-links legacy (fuera del menú del demo, ver LEGACY_SECTION_SLUGS).
  if (slug === 'equipo') return <TeamContent />;
  if (slug === 'tablero') return <TableroContent />;
  const ph = PLACEHOLDERS[slug];
  if (ph) return <AdminPlaceholder titleKey={ph.titleKey} descKey={ph.descKey} req={ph.req} icon={ph.icon} />;
  return null;
}
